import { useCallback } from "react";
import { useSelector } from "react-redux";
import { useLoading } from "tap/utils/hooks/useLoading.js";
import { useToasts } from "tap/utils/hooks/useToasts.js";
import { useWindowsActions } from "tap/store/windows/useWindows.js";
import { tapApi } from "tap/api.js";
import { filterCriteria } from "tap/utils/filterCriteria.js";

// Minimum time to show the loading spinner (in ms)
const MIN_LOADING_DURATION = 400;

/**
 * Custom hook that consolidates windows data fetching logic.
 * Handles loading states, error toasts, and Redux updates.
 *
 * @returns {Object} Object containing fetchWindows function and windows state values
 */
export function useWindowsData() {
  const { setLoadingTrue, setLoadingFalse } = useLoading();
  const { showErrorToast } = useToasts();
  const { updateWindows } = useWindowsActions();
  const { filterForm, windowsRange, windowsSort, itemsPerPage } = useSelector(
    (s) => s.windows,
  );

  /**
   * Fetches windows from the API with the given range.
   * Ensures loading spinner is visible for at least MIN_LOADING_DURATION.
   * @param {Array} range - The pagination range (e.g., [0, 24])
   * @param {Object|null} customFilterData - Optional filter data to use instead of Redux filterForm
   * @param {Array|null} customSort - Optional sort array to use instead of Redux windowsSort
   * @returns {Promise<Object|null>} The API response data or null on error
   */
  const fetchWindows = useCallback(
    async (range, customFilterData = null, customSort = null) => {
      const startTime = Date.now();
      setLoadingTrue();

      const filterData = customFilterData ?? filterForm;
      const sortData = customSort ?? windowsSort;

      try {
        const data = await tapApi.windowSequences(
          filterCriteria(filterData, "", "windowSequences"),
          range,
          sortData,
        );

        if (data?.code || data?.httpCode || data?.error) {
          showErrorToast(data?.message || "Failed to fetch windows data.");
          return null;
        }

        return data;
      } catch (error) {
        showErrorToast(error?.message || "Failed to fetch windows data.");
        return null;
      } finally {
        // Ensure minimum loading duration for consistent UX
        const elapsed = Date.now() - startTime;
        const remainingTime = MIN_LOADING_DURATION - elapsed;

        if (remainingTime > 0) {
          setTimeout(() => setLoadingFalse(), remainingTime);
        } else {
          setLoadingFalse();
        }
      }
    },
    [filterForm, windowsSort, setLoadingTrue, setLoadingFalse, showErrorToast],
  );

  return {
    fetchWindows,
    updateWindows,
    itemsPerPage,
    windowsRange,
    filterForm,
    windowsSort,
  };
}
